import { navContentStore } from "../state/nav-content-store";
import {
  constructRoutingKey,
  findTopLevelRoutingKey,
  getLastRoutingSegment,
  getModelsRoutingKey,
  removeLastRoutingSegment,
  RoutingKey,
  RoutingKeys,
} from "../entities/routing-key";
import { EventAction, EventActions } from "../utility/constants";
import {
  Content,
  findMenuItemById,
  Series,
  TMenu,
  TShopMenu,
} from "../entities/content";
import { getUsername, IsLoggedIn, isString } from "../utility/helper";
import abTestingService from "./ab-testing/ab-testing-constructor-service";
import { isCountryInOneOfRegions, splitLocale } from "../entities/locale";
import { navStateStore } from "../state/nav-state-store";
import { Application } from "../utility/constants/app";
import analyticsManager from "./analytics/analytics-manager";
import featureToggles from "./feature-toggles";

export function getSideDrawerRoutingKey(activeRoutingKey: string): string {
  const topLevelRoutingKey = findTopLevelRoutingKey(activeRoutingKey);

  switch (topLevelRoutingKey) {
    case activeRoutingKey: {
      break;
    }

    case RoutingKeys.DEALER_SEARCH: {
      return RoutingKeys.DEALER_SEARCH;
    }

    case RoutingKeys.MODELS: {
      return RoutingKeys.MODELS;
    }

    case RoutingKeys.SHOP:
    case RoutingKeys.VEHICLE_PURCHASE:
    case RoutingKeys.ELECTRIC_OWNERSHIP:
    case RoutingKeys.SERVICES:
    case RoutingKeys.EXPERIENCE: {
      const parentRoutingKey = removeLastRoutingSegment(activeRoutingKey);
      return getLastRoutingSegment(parentRoutingKey);
    }
    default:
      break;
  }
  return RoutingKeys.MAIN_MENU;
}

export interface MenuItemProps {
  elementId: string;
  routingKeys: string[];
  analyticsId: string;
  text: string;
  link?: string;
  subtext?: string;
  iconRight?: string;
  showIconModifier?: boolean;
  datalayerEventAction?: EventAction;
  onClick?: () => void;
}

export function getLevel1Items(
  content: Content,
  locale: string,
  loggedInState: string,
  unreadMessagesCount: number,
  userConsentGivenForGoogleMaps: boolean,
): MenuItemProps[] {
  const {
    dealer,
    models,
    account,
    vehiclePurchase,
    shop,
    experience,
    services,
    electricOwnership,
  } = content;
  const { app } = navStateStore.state;

  let outboundLink;
  if (locale === "en-SG") {
    // outbound link needed for Singapore market only when POC is not active
    outboundLink = dealer.search.outboundLink?.link;

    if (featureToggles.hasPorscheAtJewelsPOC()) {
      outboundLink = undefined;
    }
  }

  const dealerRoutingKeys: string[] = [RoutingKeys.DEALER_SEARCH];

  const menu: MenuItemProps[] = [];

  if (models !== null && models.text !== "") {
    const modelSection: MenuItemProps = {
      elementId: RoutingKeys.MODELS,
      routingKeys: [RoutingKeys.MODELS],
      analyticsId: models.id,
      text: models.text,
    };

    menu.unshift(modelSection);
  }

  if (
    (locale === "en-GB" ||
      locale === "en-IE" ||
      featureToggles.isNewElectricOwnershipEnabled()) &&
    electricOwnership !== null &&
    electricOwnership.text !== ""
  ) {
    menu.push({
      elementId: RoutingKeys.ELECTRIC_OWNERSHIP,
      routingKeys: [RoutingKeys.ELECTRIC_OWNERSHIP],
      analyticsId: electricOwnership.id,
      text: electricOwnership.text,
    });
  }

  if (vehiclePurchase !== null && vehiclePurchase.text !== "") {
    const vehiclePurchaseSection: MenuItemProps = {
      elementId: RoutingKeys.VEHICLE_PURCHASE,
      routingKeys: [RoutingKeys.VEHICLE_PURCHASE],
      analyticsId: vehiclePurchase.id,
      text: vehiclePurchase.text,
    };
    menu.push(vehiclePurchaseSection);
  }

  if (shop !== null && shop.text !== "") {
    const shopItem: MenuItemProps = {
      elementId: RoutingKeys.SHOP,
      routingKeys: [RoutingKeys.SHOP],
      analyticsId: shop.id,
      text: shop.text,
      link: app !== Application.shop ? shop.link : undefined,
      datalayerEventAction: EventActions.NAVIGATION_LINK_CLICK,
      iconRight: "none",
    };

    menu.push(shopItem);
  }

  if (services !== null && services.text !== "") {
    const servicesSection: MenuItemProps = {
      elementId: RoutingKeys.SERVICES,
      routingKeys: [RoutingKeys.SERVICES],
      analyticsId: services.id,
      text: services.text,
    };

    menu.push(servicesSection);
  }

  if (experience !== null && experience.text !== "") {
    const experienceSection: MenuItemProps = {
      elementId: RoutingKeys.EXPERIENCE,
      routingKeys: [RoutingKeys.EXPERIENCE],
      analyticsId: experience.id,
      text: experience.text,
    };

    menu.push(experienceSection);
  }

  let dealersearchLinkPresent = true;

  const dealerSearch: MenuItemProps = {
    elementId: RoutingKeys.DEALER_SEARCH,
    routingKeys: dealerRoutingKeys,
    analyticsId: dealer.id,
    text: dealer.text,
    link: outboundLink,
    iconRight:
      locale === "en-SG" && !featureToggles.hasPorscheAtJewelsPOC()
        ? "none"
        : "arrow-head-right",
  };

  if (
    !dealer.search.map?.link &&
    !isDealerSearchEnabled(locale, userConsentGivenForGoogleMaps)
  ) {
    // edge case for dealer search map link not present and dealer search not enabled e.g. via consent
    dealersearchLinkPresent = false;
    dealerSearch.link = undefined;
    dealerSearch.onClick = undefined;
  } else if (
    locale !== "en-SG" &&
    dealer.search.map?.link &&
    !isDealerSearchEnabled(locale, userConsentGivenForGoogleMaps)
  ) {
    dealerSearch.link = dealer.search.map.link;
    dealerSearch.onClick = undefined;
  }

  if (dealersearchLinkPresent) {
    menu.push(dealerSearch);
  }

  if (account?.text) {
    const isLoggedIn = IsLoggedIn(loggedInState);
    const username = isLoggedIn
      ? getUsername(loggedInState, account.loggedInFallback)
      : undefined;

    if (isString(account.experienceName)) {
      account.links = abTestingService().executeContentChangeAccount(
        account,
        navStateStore.state.app,
      );
    }

    menu.push({
      elementId: RoutingKeys.ACCOUNT,
      routingKeys: [RoutingKeys.ACCOUNT],
      analyticsId: account.id,
      text: account.text,
      subtext: username,
      showIconModifier: isLoggedIn && unreadMessagesCount > 0,
    });
  }

  return menu;
}

export function getSideDrawerItems(
  content: Content,
  locale: string,
  loggedInState: string,
  unreadMessagesCount: number,
  activeRoutingKey: string,
  userConsentGiven: boolean,
): MenuItemProps[] {
  const topLevelRoutingKey = findTopLevelRoutingKey(activeRoutingKey);

  switch (topLevelRoutingKey) {
    case activeRoutingKey:
      break;

    case RoutingKeys.MODELS: {
      if (content.models !== null) {
        return content.models.series.map((modelsSeries) => {
          return {
            elementId: getModelsRoutingKey(modelsSeries.id),
            routingKeys: [getModelsRoutingKey(modelsSeries.id)],
            analyticsId: modelsSeries.id,
            text: modelsSeries.name,
            subtext: modelsSeries.tags.map((tag) => tag.text).join(" | "),
            renderSignatures: false,
            datalayerEventAction: EventActions.MODEL_RANGE_CLICK,
            customDatalayerProperties: {
              car: {
                engineType: analyticsManager.getEngineTypes(modelsSeries),
              },
            },
          };
        });
      }
      break;
    }

    case RoutingKeys.SHOP:
    case RoutingKeys.VEHICLE_PURCHASE:
    case RoutingKeys.ELECTRIC_OWNERSHIP:
    case RoutingKeys.SERVICES:
    case RoutingKeys.EXPERIENCE: {
      const parentRoutingKey = removeLastRoutingSegment(activeRoutingKey);
      const menuDrawerParent = getMenuContent(
        content,
        parentRoutingKey,
        topLevelRoutingKey,
      );

      if (menuDrawerParent !== null) {
        return menuDrawerParent.children.map((childItem) => {
          return {
            elementId: constructRoutingKey(parentRoutingKey, childItem.id),
            routingKeys: [constructRoutingKey(parentRoutingKey, childItem.id)],
            analyticsId: childItem.id,
            text: childItem.text,
            link: childItem.link,
          };
        });
      }
      break;
    }
    default:
      break;
  }

  return getLevel1Items(
    content,
    locale,
    loggedInState,
    unreadMessagesCount,
    userConsentGiven,
  );
}

export function getMenuContent(
  content: Content,
  routingKey: string,
  topLevelRoutingKey: string,
): TMenu | TShopMenu | null {
  const {
    vehiclePurchase,
    experience,
    shop,
    legacyMore,
    services,
    electricOwnership,
  } = content;

  const menuContent: TMenu | undefined | null = {
    [RoutingKeys.VEHICLE_PURCHASE]: vehiclePurchase,
    [RoutingKeys.EXPERIENCE]: experience,
    [RoutingKeys.SHOP]: shop,
    [RoutingKeys.MORE]: legacyMore,
    [RoutingKeys.ELECTRIC_OWNERSHIP]: electricOwnership,
    [RoutingKeys.SERVICES]: services,
  }[topLevelRoutingKey];

  if (menuContent === undefined || menuContent === null) {
    return null;
  }

  if (isString(menuContent.experienceName)) {
    menuContent.children = abTestingService().executeContentChange(
      menuContent,
      navStateStore.state.app,
    );
  }

  const idOfLastRoutingSegment: string = getLastRoutingSegment(routingKey);
  return idOfLastRoutingSegment === topLevelRoutingKey
    ? menuContent
    : findMenuItemById(menuContent, idOfLastRoutingSegment);
}

export function isDealerSearchEnabled(
  locale: string,
  userConsentGiven: boolean,
): boolean {
  try {
    const { country } = splitLocale(locale);
    return !isCountryInOneOfRegions(country) && userConsentGiven;
  } catch {
    // ignore error
  }
  return true;
}

export function getBackButtonText(
  content: Content,
  previousRoutingKey: string,
): string {
  if (!isString(previousRoutingKey)) {
    return "";
  }

  const topLevelRoutingKey = findTopLevelRoutingKey(previousRoutingKey);

  switch (topLevelRoutingKey) {
    case RoutingKeys.MODELS: {
      return content.models ? content.models.text : "";
    }

    case RoutingKeys.DEALER_SEARCH:
      if (content.dealer.numberOfDealers === 1) {
        return "";
      }
      return content.dealer.details.backToSearch;

    case RoutingKeys.SHOP:
    case RoutingKeys.VEHICLE_PURCHASE:
    case RoutingKeys.MORE:
    case RoutingKeys.EXPERIENCE: {
      const menuContent = getMenuContent(
        navContentStore.state,
        previousRoutingKey,
        topLevelRoutingKey,
      );

      if (menuContent !== null) {
        return menuContent.text;
      }

      break;
    }

    default:
      break;
  }

  return "";
}

export function getHeadlineText(
  content: Content,
  routingKey: string,
  topLevelRoutingKey: string,
): string {
  const {
    dealer,
    account,
    models,
    vehiclePurchase,
    experience,
    shop,
    legacyMore,
    services,
    electricOwnership,
  } = content;
  let menuContent: TMenu | undefined | null | Series;
  switch (topLevelRoutingKey) {
    case RoutingKeys.MORE:
    case RoutingKeys.SHOP:
    case RoutingKeys.EXPERIENCE:
    case RoutingKeys.ELECTRIC_OWNERSHIP:
    case RoutingKeys.SERVICES:
    case RoutingKeys.VEHICLE_PURCHASE: {
      menuContent = {
        [RoutingKeys.VEHICLE_PURCHASE]: vehiclePurchase,
        [RoutingKeys.EXPERIENCE]: experience,
        [RoutingKeys.SHOP]: shop,
        [RoutingKeys.MORE]: legacyMore,
        [RoutingKeys.ELECTRIC_OWNERSHIP]: electricOwnership,
        [RoutingKeys.SERVICES]: services,
      }[topLevelRoutingKey];

      if (menuContent === undefined) {
        return "";
      }

      const idOfLastRoutingSegment: string = getLastRoutingSegment(routingKey);
      return idOfLastRoutingSegment === topLevelRoutingKey
        ? (menuContent?.text as string)
        : (findMenuItemById(menuContent, idOfLastRoutingSegment)
            ?.text as string);
    }
    case RoutingKeys.SERIES:
    case RoutingKeys.MODELS: {
      const routingId = getLastRoutingSegment(routingKey);
      menuContent = models?.series.find(
        (modelsSeries) => routingId === modelsSeries.id,
      );

      return menuContent === undefined
        ? (models?.text as string)
        : menuContent?.id;
    }
    case RoutingKeys.DEALER_SEARCH: {
      return dealer.search.text;
    }
    case RoutingKeys.ACCOUNT: {
      return account?.text as string;
    }
    default:
      break;
  }
  return "";
}

export function getSubtext(
  topLevelRoutingKey?: RoutingKey,
  activeRoutingKey?: string,
): string | undefined {
  const { account, models } = navContentStore.state;
  const { loggedInState } = navStateStore.state;

  if (topLevelRoutingKey === RoutingKeys.ACCOUNT) {
    const isLoggedIn = IsLoggedIn(loggedInState);
    return isLoggedIn
      ? getUsername(loggedInState, account?.loggedInFallback ?? "")
      : "";
  }

  if (topLevelRoutingKey === RoutingKeys.MODELS) {
    if (isString(activeRoutingKey)) {
      const routingId = getLastRoutingSegment(activeRoutingKey);
      const menuContent = models?.series.find(
        (modelsSeries) => routingId === modelsSeries.id,
      );
      if (menuContent) {
        return menuContent.tags.map((tag) => tag.text).join(" | ");
      }
    }
  }

  return "";
}

export function getRootMenuItem(
  content: Content,
  key: keyof Content,
): TMenu | null {
  if (
    !["vehiclePurchase", "shop", "experience", "services"].includes(key) ||
    content[key] === undefined
  ) {
    return null;
  }

  return content[key] as TMenu;
}

export function snakeToCamel(input: string): string {
  return input.replace(/_([a-zA-Z])/g, (_, group1) => group1.toUpperCase());
}

export function getValidContentPathSegments(
  rootContentItem: TMenu,
  segments: string[],
): string[] {
  let currentContentItem: TMenu | undefined = rootContentItem;

  const validSegments: string[] = [];
  // eslint-disable-next-line
  for (const segment of segments) {
    currentContentItem = currentContentItem.children.find(
      (child) => child.id === segment,
    );
    if (currentContentItem === undefined || currentContentItem === null) {
      break;
    }
    validSegments.push(segment);
  }

  return validSegments;
}
